import React, { useState, useEffect } from 'react';
import { Box, Typography, Modal, TextField, Button, Grid, MenuItem, FormControl, InputLabel, Select, CircularProgress, Snackbar, Alert, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { RoomService } from '../api/services/roomService';

const AMENITY_CATEGORIES = [
    "General", "F&B", "Bathroom", "Entertainment", "View", "Comfort", "Business"
];

export default function AmenityFormModal({ open, onClose, amenity = null, onRefresh }) {
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        category: 'General'
    });
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        if (amenity) {
            setFormData({
                name: amenity.name || '',
                category: amenity.category || 'General'
            });
        } else {
            setFormData({
                name: '',
                category: 'General'
            });
        }
    }, [amenity, open]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
            const tenantId = authData.tenantId || '1';

            const payload = {
                ...formData,
                id: amenity?.id
            };

            await RoomService.saveAmenity(payload, tenantId);
            
            setNotification({ 
                open: true, 
                message: `Amenity ${amenity ? 'updated' : 'created'} successfully!`, 
                severity: 'success' 
            });
            
            if (onRefresh) onRefresh();
            setTimeout(onClose, 1000);
        } catch (error) {
            console.error('Failed to save amenity:', error);
            setNotification({ 
                open: true, 
                message: error.message || 'Failed to save amenity', 
                severity: 'error' 
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Modal open={open} onClose={onClose}>
                <Box sx={{
                    position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                    width: { xs: '90%', sm: 400 }, bgcolor: 'background.paper', borderRadius: 3, p: 4, boxShadow: 24
                }}>
                    <IconButton sx={{ position: 'absolute', top: 12, right: 12 }} onClick={onClose}>
                        <CloseIcon />
                    </IconButton>

                    <Typography variant="h6" fontWeight="bold" mb={3}>
                        {amenity ? 'Edit Amenity' : 'Create New Amenity'}
                    </Typography>

                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Amenity Name"
                                    name="name"
                                    placeholder="e.g. Mini Bar"
                                    value={formData.name}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>Category</InputLabel>
                                    <Select
                                        name="category"
                                        value={formData.category}
                                        label="Category"
                                        onChange={handleChange}
                                    >
                                        {AMENITY_CATEGORIES.map((cat) => (
                                            <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>

                        <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
                            <Button fullWidth variant="outlined" onClick={onClose} disabled={loading}>Cancel</Button>
                            <Button 
                                fullWidth 
                                type="submit"
                                variant="contained" 
                                disabled={loading}
                                startIcon={loading && <CircularProgress size={20} color="inherit" />}
                            >
                                {loading ? 'Saving...' : amenity ? 'Update Amenity' : 'Create Amenity'}
                            </Button>
                        </Box>
                    </form>
                </Box>
            </Modal>

            <Snackbar
                open={notification.open}
                autoHideDuration={4000}
                onClose={() => setNotification({ ...notification, open: false })}
            >
                <Alert severity={notification.severity} sx={{ width: '100%' }}>
                    {notification.message}
                </Alert>
            </Snackbar>
        </>
    );
}
